export const ROLES = {
  CMT_ADMIN: 'cmt_admin',
  CMT_LEADER: 'cmt_leader',
  CMT_LEADER_ALT: 'cmt_leader_alt',
  CLIENT_SERVICE: 'client_service',
  SAFETY_SECURITY: 'safety_security',
  FACILITIES: 'facilities',
  TALENT_HR: 'talent_hr',
  LEGAL: 'legal',
  COMMS_MEDIA: 'comms_media',
  IT_TECH: 'it_tech',
  FINANCE: 'finance',
  OTHER: 'other',
  DEVELOPER: 'developer'
};

export const ROLE_PERMISSIONS = {
  [ROLES.CMT_ADMIN]: {
    name: 'CMT Admin',
    permissions: [
      'manage_users',
      'manage_roles',
      'manage_subscription',
      'manage_documents',
      'view_analytics',
      'manage_settings',
      'view_logs',
      'export_logs',
      'manage_organisation',
      'manage_crisis',
      'manage_teams',
      'manage_templates',
      'manage_workflows'
    ]
  },
  [ROLES.CMT_LEADER]: {
    name: 'CMT Leader',
    permissions: [
      'manage_documents',
      'view_analytics',
      'view_logs',
      'export_logs',
      'manage_team',
      'manage_crisis',
      'manage_templates',
      'manage_workflows'
    ]
  },
  [ROLES.CMT_LEADER_ALT]: {
    name: 'CMT Leader (alternate)',
    permissions: [
      'manage_documents',
      'view_analytics',
      'view_logs',
      'export_logs',
      'manage_team',
      'manage_crisis',
      'manage_templates',
      'manage_workflows'
    ]
  },
  [ROLES.CLIENT_SERVICE]: {
    name: 'Client Service',
    permissions: [
      'view_documents',
      'edit_documents',
      'view_analytics',
      'manage_clients',
      'view_logs'
    ]
  },
  [ROLES.SAFETY_SECURITY]: {
    name: 'Safety/Security',
    permissions: [
      'view_documents',
      'edit_documents',
      'view_analytics',
      'manage_security',
      'view_logs',
      'manage_crisis'
    ]
  },
  [ROLES.FACILITIES]: {
    name: 'Facilities',
    permissions: [
      'view_documents',
      'edit_documents',
      'view_analytics',
      'manage_facilities',
      'view_logs'
    ]
  },
  [ROLES.TALENT_HR]: {
    name: 'Talent/HR',
    permissions: [
      'view_documents',
      'edit_documents',
      'view_analytics',
      'manage_hr',
      'view_logs',
      'manage_users'
    ]
  },
  [ROLES.LEGAL]: {
    name: 'Legal',
    permissions: [
      'view_documents',
      'edit_documents',
      'view_analytics',
      'manage_legal',
      'view_logs',
      'manage_compliance'
    ]
  },
  [ROLES.COMMS_MEDIA]: {
    name: 'Comms/Media/PR',
    permissions: [
      'view_documents',
      'edit_documents',
      'view_analytics',
      'manage_communications',
      'view_logs',
      'manage_media'
    ]
  },
  [ROLES.IT_TECH]: {
    name: 'IT/Tech',
    permissions: [
      'view_documents',
      'edit_documents',
      'view_analytics',
      'manage_tech',
      'view_logs',
      'manage_systems'
    ]
  },
  [ROLES.FINANCE]: {
    name: 'Finance',
    permissions: [
      'view_documents',
      'edit_documents',
      'view_analytics',
      'manage_finance',
      'view_logs',
      'manage_budget'
    ]
  },
  [ROLES.OTHER]: {
    name: 'Other',
    permissions: [
      'view_documents',
      'edit_documents',
      'view_analytics',
      'view_logs'
    ]
  },
  [ROLES.DEVELOPER]: {
    name: 'Developer',
    permissions: [
      'manage_users',
      'manage_roles',
      'manage_subscription',
      'manage_documents',
      'view_analytics',
      'manage_settings',
      'view_logs',
      'export_logs',
      'manage_organisation',
      'manage_crisis',
      'manage_teams',
      'manage_templates',
      'manage_workflows',
      'manage_systems',
      'manage_tech',
      'manage_developers'
    ]
  }
};

// Role validation and normalization functions
export function normalizeRole(role) {
  if (!role) return null;
  return role.toLowerCase().trim();
}

export function isValidRole(role) {
  const normalizedRole = normalizeRole(role);
  return normalizedRole && Object.values(ROLES).includes(normalizedRole);
}

export function validateRole(role) {
  const normalizedRole = normalizeRole(role);
  if (!normalizedRole) {
    throw new Error('Role cannot be empty');
  }
  if (!isValidRole(normalizedRole)) {
    throw new Error(`Invalid role: ${role}. Must be one of: ${Object.values(ROLES).join(', ')}`);
  }
  return normalizedRole;
}

export function isCmtRole(role) {
  const normalizedRole = normalizeRole(role);
  return normalizedRole && [
    ROLES.CMT_ADMIN,
    ROLES.CMT_LEADER,
    ROLES.CMT_LEADER_ALT
  ].includes(normalizedRole);
}

export function isAdminRole(role) {
  const normalizedRole = normalizeRole(role);
  return normalizedRole === ROLES.CMT_ADMIN;
}

export function isLeaderRole(role) {
  const normalizedRole = normalizeRole(role);
  return normalizedRole === ROLES.CMT_LEADER;
}

export function isLeaderAltRole(role) {
  const normalizedRole = normalizeRole(role);
  return normalizedRole === ROLES.CMT_LEADER_ALT;
}

export function validateRolePermissions(role, permissions) {
  const normalizedRole = validateRole(role);
  const rolePermissions = ROLE_PERMISSIONS[normalizedRole]?.permissions || [];
  
  const invalidPermissions = permissions.filter(p => !rolePermissions.includes(p));
  if (invalidPermissions.length > 0) {
    throw new Error(`Invalid permissions for role ${role}: ${invalidPermissions.join(', ')}`);
  }
  
  return true;
}

export function getRolePermissions(role) {
  return ROLE_PERMISSIONS[role]?.permissions || [];
}

export function hasPermission(role, permission) {
  const permissions = getRolePermissions(role);
  return permissions.includes(permission);
}

export function getRoleName(role) {
  return ROLE_PERMISSIONS[role]?.name || 'Unknown Role';
}

export function getAllRoles() {
  return Object.entries(ROLE_PERMISSIONS).map(([key, value]) => ({
    id: key,
    name: value.name,
    permissions: value.permissions
  }));
}

// CMT functional role mappings for better display
const CMT_FUNCTIONAL_ROLES = {
  'Leader': 'CMT Leader',
  'Alternate': 'CMT Alternate',
  'Comms/Media': 'Communications/Media',
  'Security': 'Security',
  'IT': 'IT/Technology',
  'HR': 'Human Resources',
  'Legal': 'Legal',
  'Finance': 'Finance',
  'Operations': 'Operations',
  'Other': 'Other'
};

/**
 * Format any role (system or CMT functional) into a human-readable display name
 * @param {string} role - The role to format
 * @param {string} cmtRole - Optional CMT functional role
 * @returns {string} - Formatted role name
 */
export function formatRoleDisplay(role, cmtRole = null) {
  // If we have a CMT functional role, prioritize that with proper formatting
  if (cmtRole && cmtRole.trim()) {
    const formattedCmtRole = CMT_FUNCTIONAL_ROLES[cmtRole] || cmtRole;
    return formattedCmtRole;
  }
  
  // Handle system roles
  if (role && role.trim()) {
    // First try to get the role name from ROLE_PERMISSIONS
    const systemRoleName = getRoleName(role);
    if (systemRoleName !== 'Unknown Role') {
      return systemRoleName;
    }
    
    // Fallback: format the role string nicely
    return role
      .replace(/_/g, ' ')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }
  
  // Default fallback
  return 'User';
}

/**
 * Get role color for display badges
 * @param {string} role - The role to get color for
 * @param {string} cmtRole - Optional CMT functional role
 * @returns {string} - CSS color class or hex color
 */
export function getRoleColor(role, cmtRole = null) {
  // CMT roles get special colors
  if (cmtRole) {
    switch (cmtRole.toLowerCase()) {
      case 'leader':
        return '#f5222d'; // Red for leaders
      case 'alternate':
        return '#fa8c16'; // Orange for alternates
      case 'comms/media':
        return '#1890ff'; // Blue for communications/media
      case 'security':
        return '#722ed1'; // Purple for security
      case 'it':
        return '#13c2c2'; // Cyan for IT
      case 'hr':
        return '#52c41a'; // Green for HR
      case 'legal':
        return '#eb2f96'; // Pink for legal
      case 'finance':
        return '#389e0d'; // Dark green for finance
      case 'operations':
        return '#faad14'; // Gold for operations
      default:
        return '#595959'; // Gray for other
    }
  }
  
  // System roles
  if (role) {
    switch (role.toLowerCase()) {
      case 'cmt_admin':
        return '#f5222d';
      case 'cmt_leader':
        return '#fa8c16';
      case 'cmt_leader_alt':
        return '#faad14';
      case 'developer':
        return '#722ed1';
      default:
        return '#1890ff';
    }
  }
  
  return '#52c41a'; // Default green
}

/**
 * 🎯 MISSION CRITICAL: CMT Membership Validation
 * 
 * This function determines if a user role grants CMT-level permissions.
 * 
 * ⚠️ SECURITY NOTICE: This is the SINGLE SOURCE OF TRUTH for CMT membership.
 * Any changes to this function affect permission validation across the entire platform.
 * 
 * @param {string} userRole - The user's role
 * @returns {boolean} - Whether the role grants CMT member permissions
 */
export function isCMTMember(userRole) {
  if (!userRole || typeof userRole !== 'string') {
    return false;
  }
  
  // Authoritative list of roles that grant CMT-level permissions
  const cmtRoles = [
    // Leadership roles
    ROLES.CMT_ADMIN,           // 'cmt_admin'
    ROLES.CMT_LEADER,          // 'cmt_leader' 
    ROLES.CMT_LEADER_ALT,      // 'cmt_leader_alt'
    
    // Functional specialist roles that are part of CMT
    'crisis_manager',          // Crisis management specialist
    'safety_officer',          // Safety and security specialist
    'legal_compliance',        // Legal and compliance specialist
    
    // Technical and operational roles from ROLES constants
    ROLES.SAFETY_SECURITY,     // 'safety_security'
    ROLES.FACILITIES,          // 'facilities'
    ROLES.TALENT_HR,           // 'talent_hr' 
    ROLES.LEGAL,               // 'legal'
    ROLES.COMMS_MEDIA,         // 'comms_media'
    ROLES.IT_TECH,             // 'it_tech'
    ROLES.FINANCE,             // 'finance'
    ROLES.CLIENT_SERVICE,      // 'client_service'
    ROLES.OTHER                // 'other'
  ];
  
  return cmtRoles.includes(userRole.toLowerCase().trim());
} 